import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Form,
  Input,
  Select,
  Avatar,
  Tag,
  Divider,
  Spin,
  Alert,
  Row,
  Col,
} from 'antd';
import { UserOutlined, LockOutlined, SaveOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/TranslationContext';
import dashboardService from '../../service';
import { LANGUAGES } from '../../i18n/languages';

function SettingsPage() {
  const { user, logout } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [showNoteField, setShowNoteField] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoadingProfile(true);
      const data = await dashboardService.getProfile();

      if (data.success) {
        setShowNoteField(data.user.note !== undefined);
        profileForm.setFieldsValue({
          firstName: data.user.firstName || '',
          lastName: data.user.lastName || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          language: data.user.language || 'en',
          note: data.user.note !== undefined ? data.user.note || '' : '',
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile information' });
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleUpdateProfile = async (values) => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        language: values.language,
      };
      if (values.note !== undefined) {
        updateData.note = values.note;
      }

      const data = await dashboardService.updateProfile(updateData);

      if (data.success) {
        setMessage({ type: 'success', text: '✅ Profile updated successfully!' });
        localStorage.setItem('currentUser', JSON.stringify(data.user));
      } else {
        setMessage({ type: 'error', text: '❌ Failed to update profile: ' + data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Error updating profile: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      setPasswordMessage({ type: 'error', text: '❌ New passwords do not match' });
      return;
    }
    if (values.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: '❌ Password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const data = await dashboardService.changePassword(
        values.currentPassword,
        values.newPassword
      );

      if (data.success) {
        setPasswordMessage({
          type: 'success',
          text: '✅ Password changed successfully! Please login again.',
        });
        passwordForm.resetFields();
        setTimeout(() => logout(), 2000);
      } else {
        setPasswordMessage({ type: 'error', text: '❌ ' + data.error });
      }
    } catch (error) {
      setPasswordMessage({ type: 'error', text: '❌ Error changing password: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const getNoteVisibilityText = () => {
    switch (user?.role) {
      case 'manager':
        return 'Visible to you and super admin';
      case 'dealer':
        return 'Visible to you and super admin';
      case 'subdealer':
        return 'Visible to managers, dealers, and super admin';
      default:
        return '';
    }
  };

  if (loadingProfile) {
    return (
      <Flex justify="center" align="center" style={{ height: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Flex vertical gap={8} style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Account Settings
        </Typography.Title>
        <Typography.Text type="secondary">
          Manage your profile information and security settings
        </Typography.Text>
      </Flex>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card>
            <Flex align="center" gap={16} style={{ marginBottom: 24 }}>
              <Avatar
                icon={<UserOutlined />}
                style={{ backgroundColor: '#1e3a5f', width: 48, height: 48 }}
              />
              <Typography.Title level={5} style={{ margin: 0 }}>
                Profile Information
              </Typography.Title>
            </Flex>

            {message.text && (
              <Alert type={message.type} message={message.text} style={{ marginBottom: 24 }} />
            )}

            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleUpdateProfile}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="email" label="Email" rules={[{ required: true }]}>
                    <Input type="email" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="phone" label="Phone">
                    <Input type="tel" placeholder="+995 555 111 111" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="language"
                    label="Language / ენა / Язык / Լեզու / Тил / Тіл"
                  >
                    <Select
                      options={LANGUAGES.map((lang) => ({
                        label: `${lang.flag} ${lang.name}`,
                        value: lang.code,
                      }))}
                    />
                  </Form.Item>
                </Col>
                {showNoteField && (
                  <Col span={24}>
                    <Form.Item
                      name="note"
                      label="My Note"
                      extra={getNoteVisibilityText()}
                    >
                      <Input.TextArea rows={3} placeholder="Add a note about yourself..." />
                    </Form.Item>
                  </Col>
                )}
                <Col span={24}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={loading ? <Spin size="small" /> : <SaveOutlined />}
                    disabled={loading}
                    block
                  >
                    {loading ? 'Saving...' : 'Save Profile'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card style={{ marginTop: 24 }}>
            <Flex align="center" gap={16} style={{ marginBottom: 24 }}>
              <Avatar
                icon={<LockOutlined />}
                style={{ backgroundColor: '#ef4444', width: 48, height: 48 }}
              />
              <Typography.Title level={5} style={{ margin: 0 }}>
                Change Password
              </Typography.Title>
            </Flex>

            {passwordMessage.text && (
              <Alert
                type={passwordMessage.type}
                message={passwordMessage.text}
                style={{ marginBottom: 24 }}
              />
            )}

            <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[{ required: true }, { min: 6, message: 'Minimum 6 characters' }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                rules={[{ required: true }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  danger
                  htmlType="submit"
                  icon={loading ? <Spin size="small" /> : <LockOutlined />}
                  disabled={loading}
                  block
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card>
            <Typography.Title level={5} style={{ marginBottom: 24 }}>
              Account Information
            </Typography.Title>

            <Flex vertical gap={8} style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Username</Typography.Text>
              <Typography.Text strong>{user?.user_name || user?.username}</Typography.Text>
            </Flex>
            <Divider />
            <Flex vertical gap={8} style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">Role</Typography.Text>
              <Tag
                color={
                  user?.role === 'super_admin'
                    ? 'red'
                    : user?.role === 'manager'
                      ? 'blue'
                      : user?.role === 'dealer'
                        ? 'purple'
                        : 'default'
                }
              >
                {user?.role?.replace('_', ' ').toUpperCase()}
              </Tag>
            </Flex>
            <Divider />
            <Flex vertical gap={8} style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">User ID</Typography.Text>
              <Typography.Text strong>{user?.user_id || user?.id}</Typography.Text>
            </Flex>
            {user?.branchName && (
              <>
                <Divider />
                <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                  <Typography.Text type="secondary">Branch</Typography.Text>
                  <Typography.Text strong>{user.branchName}</Typography.Text>
                </Flex>
              </>
            )}
            {user?.managerName && (
              <>
                <Divider />
                <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                  <Typography.Text type="secondary">Manager</Typography.Text>
                  <Typography.Text strong>{user.managerName}</Typography.Text>
                </Flex>
              </>
            )}
            {user?.dealerName && (
              <>
                <Divider />
                <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                  <Typography.Text type="secondary">Dealer</Typography.Text>
                  <Typography.Text strong>{user.dealerName}</Typography.Text>
                </Flex>
              </>
            )}
          </Card>

          <Card style={{ marginTop: 16, background: 'var(--colorInfoBg)' }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              Security Tips
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              • Use a strong, unique password<br />
              • Change your password regularly<br />
              • Never share your credentials<br />
              • Log out from shared devices
            </Typography.Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default SettingsPage;
